import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FileSignature, ArrowRight, ArrowLeft, Check, Download, Home, ShieldCheck, Loader2, Eraser } from "lucide-react";
import { fetchListing, API } from "./api";
import { authApi, useAuth } from "./authApi";

const inp = "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white outline-none focus:border-[#C19A6B] transition-colors placeholder:text-zinc-600";
const lbl = "block text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5";
const FIN = [{ v: "comptant", l: "Comptant" }, { v: "pret", l: "Prêt bancaire" }, { v: "mixte", l: "Mixte (apport + prêt)" }, { v: "autre", l: "Autre" }];

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#111"; ctx.lineWidth = 2; ctx.lineCap = "round";
  }, []);
  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div>
      <div className="bg-white border border-white/15 relative">
        <canvas ref={canvasRef} width={520} height={180} className="w-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="signature-canvas" />
        <button type="button" onClick={clear} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-500 hover:text-black bg-white/80 px-2 py-1" data-testid="signature-clear"><Eraser className="w-3 h-3" /> Effacer</button>
      </div>
      <p className="text-[11px] text-zinc-600 mt-1">Signez avec la souris ou le doigt.</p>
    </div>
  );
}

export default function OfferForm() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(undefined);
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [buyer, setBuyer] = useState({ first_name: "", last_name: "", address: "", phone: "", email: "" });
  const [offer, setOffer] = useState({ amount: "", financing: "pret", deposit_amount: "", validity_date: "", conditions: "", comments: "" });
  const [sig, setSig] = useState({ name: "", image: "", consent: false });

  useEffect(() => { fetchListing(slug).then(setListing); }, [slug]);
  useEffect(() => {
    if (!user) return;
    authApi.myAcquereurs().then((list) => {
      const a = Array.isArray(list) ? list[0] : (list?.acquereurs || [])[0];
      if (a) setBuyer((b) => ({ ...b, first_name: a.first_name || b.first_name, last_name: a.last_name || b.last_name, email: a.email || b.email, phone: a.phone || b.phone }));
    }).catch(() => {});
  }, [user]);

  if (listing === undefined) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" /></div>;
  if (listing === null) return <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center gap-4"><p className="text-zinc-400">Ce bien n'est plus disponible.</p><Link to="/biens-off-market" className="text-[#C19A6B] underline">Voir les biens</Link></div>;

  const next = () => {
    setErr("");
    if (step === 1) {
      if (!buyer.first_name || !buyer.last_name || !buyer.email || !buyer.phone) return setErr("Merci de compléter nom, prénom, e-mail et téléphone.");
    }
    if (step === 2 && !offer.amount) return setErr("Le montant de votre offre est obligatoire.");
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setErr("");
    if (!sig.consent || !sig.name.trim()) return setErr("Merci de saisir votre nom et de cocher le consentement.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/offers`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ listing_slug: slug, buyer, offer, signature: { method: sig.image ? "drawn" : "typed", name: sig.name, image: sig.image, consent: true } }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Envoi impossible"); }
      setDone(await res.json());
    } catch (e) { setErr(e.message || "Envoi impossible"); }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center border border-white/10 bg-[#0d0d10] p-8" data-testid="offer-success">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7 text-emerald-400" /></div>
          <h1 className="text-2xl font-light mb-2">Offre transmise ✍️</h1>
          <p className="text-sm text-zinc-400">Votre offre officielle <span className="text-[#C19A6B]">{done.ref}</span> a été signée et envoyée au conseiller. Vous recevrez un e-mail à chaque étape.</p>
          <div className="flex flex-col gap-2 mt-6">
            <a href={`${API}/offers/${done.id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="offer-download-pdf" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors"><Download className="w-4 h-4" /> Télécharger mon offre (PDF)</a>
            <Link to={`/bien/${slug}`} className="inline-flex items-center justify-center gap-2 border border-white/15 text-zinc-300 uppercase tracking-wider text-sm px-6 py-3 hover:border-[#C19A6B] hover:text-[#C19A6B] transition-colors"><Home className="w-4 h-4" /> Retour à l'annonce</Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const fmt = (n) => { try { return `${parseInt(n).toLocaleString("fr-FR")} €`; } catch { return n; } };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-28 pb-20 px-6" data-testid="offer-form">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => nav(`/bien/${slug}`)} className="text-xs uppercase tracking-wider text-zinc-500 hover:text-[#C19A6B] inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Retour à l'annonce</button>
        <div className="flex items-center gap-3 mb-2"><FileSignature className="w-6 h-6 text-[#C19A6B]" /><h1 className="text-3xl font-light">Faire une offre officielle</h1></div>
        <p className="text-sm text-zinc-400 mb-8">{listing.title} — {listing.city} · <span className="text-[#C19A6B]">{fmt(listing.price)}</span></p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8">
          {["Vos informations", "Votre offre", "Signature"].map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 ${step >= i + 1 ? "bg-[#C19A6B] text-white" : "bg-white/10 text-zinc-500"}`}>{step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}</div>
              <span className={`text-[11px] uppercase tracking-wider ${step >= i + 1 ? "text-white" : "text-zinc-600"} hidden sm:inline`}>{s}</span>
              {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? "bg-[#C19A6B]" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {err && <p className="text-sm text-red-400 mb-4 border border-red-400/30 bg-red-500/5 px-3 py-2" data-testid="offer-error">{err}</p>}

        {step === 1 && (
          <div className="space-y-4" data-testid="offer-step-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Prénom *</label><input value={buyer.first_name} onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })} className={inp} data-testid="buyer-first-name" /></div>
              <div><label className={lbl}>Nom *</label><input value={buyer.last_name} onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })} className={inp} data-testid="buyer-last-name" /></div>
            </div>
            <div><label className={lbl}>Adresse</label><input value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} className={inp} data-testid="buyer-address" /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Téléphone *</label><input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} className={inp} data-testid="buyer-phone" /></div>
              <div><label className={lbl}>E-mail *</label><input type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className={inp} data-testid="buyer-email" /></div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="offer-step-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={lbl}>Montant proposé (€) *</label><input type="number" value={offer.amount} onChange={(e) => setOffer({ ...offer, amount: e.target.value })} className={inp} data-testid="offer-amount" /></div>
              <div><label className={lbl}>Apport (€) — optionnel</label><input type="number" value={offer.deposit_amount} onChange={(e) => setOffer({ ...offer, deposit_amount: e.target.value })} className={inp} data-testid="offer-deposit" /></div>
            </div>
            <div>
              <label className={lbl}>Financement</label>
              <div className="flex flex-wrap gap-2">
                {FIN.map((f) => <button key={f.v} type="button" onClick={() => setOffer({ ...offer, financing: f.v })} data-testid={`offer-fin-${f.v}`} className={`text-xs uppercase tracking-wider px-3 py-2 border ${offer.financing === f.v ? "border-[#C19A6B] text-[#C19A6B] bg-[#C19A6B]/10" : "border-white/15 text-zinc-400"}`}>{f.l}</button>)}
              </div>
            </div>
            <div><label className={lbl}>Date de validité de l'offre</label><input type="date" value={offer.validity_date} onChange={(e) => setOffer({ ...offer, validity_date: e.target.value })} className={inp} data-testid="offer-validity" /></div>
            <div><label className={lbl}>Conditions particulières</label><textarea rows={3} value={offer.conditions} onChange={(e) => setOffer({ ...offer, conditions: e.target.value })} className={inp} data-testid="offer-conditions" placeholder="Ex : sous condition d'obtention de prêt, vente du bien actuel…" /></div>
            <div><label className={lbl}>Commentaires</label><textarea rows={2} value={offer.comments} onChange={(e) => setOffer({ ...offer, comments: e.target.value })} className={inp} data-testid="offer-comments" /></div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="offer-step-3">
            <div className="border border-white/10 p-4 text-sm space-y-1.5">
              <p className="text-[11px] uppercase tracking-wider text-[#C19A6B] mb-2">Récapitulatif</p>
              <div className="flex justify-between"><span className="text-zinc-500">Acquéreur</span><span>{buyer.first_name} {buyer.last_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Montant proposé</span><span className="text-[#C19A6B]">{fmt(offer.amount)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Financement</span><span>{FIN.find((f) => f.v === offer.financing)?.l}</span></div>
              {offer.validity_date && <div className="flex justify-between"><span className="text-zinc-500">Validité</span><span>{offer.validity_date}</span></div>}
            </div>
            <div>
              <label className={lbl}>Nom du signataire *</label>
              <input value={sig.name} onChange={(e) => setSig({ ...sig, name: e.target.value })} className={inp} data-testid="signature-name" placeholder="Prénom Nom" />
            </div>
            <div>
              <label className={lbl}>Votre signature</label>
              <SignaturePad onChange={(image) => setSig((s) => ({ ...s, image }))} />
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="signature-consent">
              <input type="checkbox" checked={sig.consent} onChange={(e) => setSig({ ...sig, consent: e.target.checked })} className="accent-[#C19A6B] mt-1" />
              <span>Je certifie l'exactitude des informations et je signe électroniquement cette offre d'achat. J'accepte que l'horodatage et mon adresse IP soient enregistrés à des fins de traçabilité.</span>
            </label>
            <p className="text-[11px] text-zinc-600 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#C19A6B]" /> Cette offre ne vaut pas compromis de vente.</p>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-8">
          {step > 1 ? <button onClick={() => { setErr(""); setStep((s) => s - 1); }} className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-zinc-400 hover:text-white" data-testid="offer-prev"><ArrowLeft className="w-4 h-4" /> Précédent</button> : <span />}
          {step < 3
            ? <button onClick={next} data-testid="offer-next" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Continuer <ArrowRight className="w-4 h-4" /></button>
            : <button onClick={submit} disabled={submitting} data-testid="offer-submit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Signer et envoyer l'offre</button>}
        </div>
      </div>
    </div>
  );
}
